import { compareCodes } from '../util.js';
import { stepConfirmation } from './confirmation.js';
import {
  futureChangeTags,
  knowledgeCitations,
  type FutureStepTag,
  type FutureTag,
} from './future.js';
import {
  STEP_TYPES,
  type KnowledgeCitation,
  type MapModel,
  type MapStep,
  type StepType,
} from './model.js';
import type { Bounds, MapLayout, Point } from './layout.js';

/**
 * BPMN 2.0 generated from the model and its layout (ADR-017 §2): one
 * collaboration with one participant (the client), a lane per role, flow
 * nodes by type, sequence flows with their labels, and DI shapes and edges
 * from the same layout. Each step's system and citations are written as
 * `bpmn:documentation`, so the file is useful outside Caliber. Generated on
 * every read and download; never stored.
 */

export interface BpmnOptions {
  /** The process, e.g. "Invoice to payment". */
  processName: string;
  /** The pool: the client whose process it is. */
  participantName: string;
  /**
   * Write each step's system and citations as `bpmn:documentation` (default
   * true). The client's validation link sets it to false: its drawing carries
   * no evidence numbers or notes (rule 16).
   */
  documentation?: boolean;
  /**
   * A future-state map (E13, ADR-020 §7): each step's documentation names its
   * change against `current` and each knowledge item it cites, written by
   * `renderCitation` in the client form (vendor-confidential items as title
   * and date, protected names removed: rule 21), and any assumption.
   */
  future?: {
    current: MapModel;
    renderCitation: (citation: KnowledgeCitation) => string;
  };
}

/** The bpmn-js type of each step type. */
export const BPMN_TYPES: Record<StepType, string> = {
  start_event: 'bpmn:StartEvent',
  end_event: 'bpmn:EndEvent',
  task: 'bpmn:Task',
  user_task: 'bpmn:UserTask',
  manual_task: 'bpmn:ManualTask',
  service_task: 'bpmn:ServiceTask',
  exclusive_gateway: 'bpmn:ExclusiveGateway',
};

/** The step type for a bpmn-js type, or null for anything the map does not hold. */
export function stepTypeOfBpmn(type: unknown): StepType | null {
  return STEP_TYPES.find((t) => BPMN_TYPES[t] === type) ?? null;
}

export type BpmnElementKind = 'lane' | 'step' | 'flow';

const PREFIX: Record<BpmnElementKind, string> = { lane: 'lane-', step: 'step-', flow: 'flow-' };

/** The fixed id of the pool. */
export const PARTICIPANT_ID = 'participant';

/**
 * The element id of a lane, step or flow. Lanes, steps and flows each have
 * their own keys, so the id carries the kind; bpmn-js never makes ids of this
 * form (it uses `Activity_0x1y2z3`), so a new canvas element is never mistaken
 * for one of ours.
 */
export function bpmnId(kind: BpmnElementKind, key: string): string {
  return PREFIX[kind] + key;
}

/** The kind and key behind an element id, or null for anything that is not ours. */
export function parseBpmnId(
  id: unknown,
): { kind: BpmnElementKind | 'participant'; key: string } | null {
  if (typeof id !== 'string') return null;
  if (id === PARTICIPANT_ID) return { kind: 'participant', key: id };
  for (const kind of ['lane', 'step', 'flow'] as const) {
    if (id.startsWith(PREFIX[kind]) && id.length > PREFIX[kind].length)
      return { kind, key: id.slice(PREFIX[kind].length) };
  }
  return null;
}

export function toBpmnXml(model: MapModel, layout: MapLayout, options: BpmnOptions): string {
  const steps = new Map(model.steps.map((s) => [s.key, s]));
  const flows = model.flows.filter((f) => f.from !== f.to && steps.has(f.from) && steps.has(f.to));
  const out: string[] = [];
  const line = (depth: number, text: string) => out.push('  '.repeat(depth) + text);

  line(0, '<?xml version="1.0" encoding="UTF-8"?>');
  line(
    0,
    '<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL" ' +
      'xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" ' +
      'xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" ' +
      'xmlns:di="http://www.omg.org/spec/DD/20100524/DI" ' +
      'id="definitions" targetNamespace="https://caliber.averoadvisors.com/bpmn" ' +
      'exporter="Caliber 2.0" exporterVersion="1">',
  );
  line(1, '<bpmn:collaboration id="collaboration">');
  line(
    2,
    `<bpmn:participant id="${PARTICIPANT_ID}" name="${attr(options.participantName)}" processRef="process" />`,
  );
  line(1, '</bpmn:collaboration>');

  line(1, `<bpmn:process id="process" name="${attr(options.processName)}" isExecutable="false">`);
  line(2, '<bpmn:laneSet id="lane-set">');
  for (const lane of model.lanes) {
    const members = model.steps.filter((s) => s.lane === lane.key);
    if (members.length === 0) {
      line(3, `<bpmn:lane id="${bpmnId('lane', lane.key)}" name="${attr(lane.name)}" />`);
      continue;
    }
    line(3, `<bpmn:lane id="${bpmnId('lane', lane.key)}" name="${attr(lane.name)}">`);
    for (const s of members)
      line(4, `<bpmn:flowNodeRef>${bpmnId('step', s.key)}</bpmn:flowNodeRef>`);
    line(3, '</bpmn:lane>');
  }
  line(2, '</bpmn:laneSet>');

  const tags = options.future ? futureChangeTags(model, options.future.current).steps : null;
  for (const step of model.steps) {
    const tag = elementName(step.type);
    const notes =
      options.documentation === false
        ? []
        : tags && options.future
          ? futureStepDocumentation(step, tags[step.key]!, options.future.renderCitation)
          : stepDocumentation(step);
    const ins = flows.filter((f) => f.to === step.key);
    const outs = flows.filter((f) => f.from === step.key);
    const open = `<${tag} id="${bpmnId('step', step.key)}" name="${attr(step.name)}"`;
    if (notes.length === 0 && ins.length === 0 && outs.length === 0) {
      line(2, `${open} />`);
      continue;
    }
    line(2, `${open}>`);
    if (notes.length > 0)
      line(3, `<bpmn:documentation>${text(notes.join('\n'))}</bpmn:documentation>`);
    for (const f of ins) line(3, `<bpmn:incoming>${bpmnId('flow', f.key)}</bpmn:incoming>`);
    for (const f of outs) line(3, `<bpmn:outgoing>${bpmnId('flow', f.key)}</bpmn:outgoing>`);
    line(2, `</${tag}>`);
  }

  for (const f of flows) {
    const name = f.label ? ` name="${attr(f.label)}"` : '';
    line(
      2,
      `<bpmn:sequenceFlow id="${bpmnId('flow', f.key)}"${name} sourceRef="${bpmnId('step', f.from)}" targetRef="${bpmnId('step', f.to)}" />`,
    );
  }
  line(1, '</bpmn:process>');

  line(1, '<bpmndi:BPMNDiagram id="diagram">');
  line(2, '<bpmndi:BPMNPlane id="plane" bpmnElement="collaboration">');
  shape(line, PARTICIPANT_ID, layout.pool, ' isHorizontal="true"');
  for (const lane of model.lanes) {
    const bounds = layout.lanes.get(lane.key);
    if (bounds) shape(line, bpmnId('lane', lane.key), bounds, ' isHorizontal="true"');
  }
  for (const step of model.steps) {
    const bounds = layout.steps.get(step.key);
    const extra = step.type === 'exclusive_gateway' ? ' isMarkerVisible="true"' : '';
    if (bounds) shape(line, bpmnId('step', step.key), bounds, extra);
  }
  for (const f of flows) {
    const points = layout.flows.get(f.key);
    if (!points) continue;
    const id = bpmnId('flow', f.key);
    line(3, `<bpmndi:BPMNEdge id="di-${id}" bpmnElement="${id}">`);
    for (const p of points) line(4, waypoint(p));
    line(3, '</bpmndi:BPMNEdge>');
  }
  line(2, '</bpmndi:BPMNPlane>');
  line(1, '</bpmndi:BPMNDiagram>');
  line(0, '</bpmn:definitions>');
  return out.join('\n') + '\n';
}

/**
 * The documentation lines for a step: where it is done, what it cites, who
 * validated it, and whether it is still unconfirmed.
 */
export function stepDocumentation(step: MapStep): string[] {
  const lines: string[] = [];
  if (step.system) lines.push(`Done in ${step.system}`);
  const evidence = step.citations
    .flatMap((c) => (c.kind === 'discovery_evidence' && c.evidence_number ? [c] : []))
    .sort((a, b) => compareCodes(a.evidence_number!, b.evidence_number!))
    .map((c) =>
      c.evidence_status === 'confirmed'
        ? c.evidence_number!
        : `${c.evidence_number} (${c.evidence_status})`,
    );
  if (evidence.length > 0) lines.push(`Evidence ${evidence.join(', ')}`);
  for (const c of step.citations) {
    if (c.kind !== 'client_validation') continue;
    const when = c.validated_at ? `, ${dayOf(c.validated_at)}` : '';
    lines.push(`Validated by ${c.validated_by ?? 'the client'}${when}`);
  }
  if (stepConfirmation(step).confirmation === 'unconfirmed')
    lines.push('Unconfirmed: no confirmed evidence or client validation yet');
  return lines;
}

const TAG_WORDS: Record<FutureTag, string> = {
  new: 'New',
  changed: 'Changed',
  automated: 'Automated',
  unchanged: 'Unchanged',
};

/**
 * The documentation lines for a future-state step: its change against the
 * current state, where it is done, each knowledge item it cites (rendered
 * by the caller for its audience), and whether it is an assumption.
 */
export function futureStepDocumentation(
  step: MapStep,
  tag: FutureStepTag,
  renderCitation: (citation: KnowledgeCitation) => string,
): string[] {
  const lines = [`${TAG_WORDS[tag.tag]}${tag.workaround ? ', with a workaround' : ''}`];
  if (step.system) lines.push(`Done in ${step.system}`);
  for (const c of knowledgeCitations(step)) lines.push(renderCitation(c));
  if (step.is_assumption === true) lines.push('Assumption: no vendor evidence cited');
  return lines;
}

/** `5 Oct 2026`, in UTC so the file is the same wherever it is made. */
function dayOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const month = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ][d.getUTCMonth()];
  return `${d.getUTCDate()} ${month} ${d.getUTCFullYear()}`;
}

function elementName(type: StepType): string {
  const local = BPMN_TYPES[type].slice('bpmn:'.length);
  return `bpmn:${local.charAt(0).toLowerCase()}${local.slice(1)}`;
}

function shape(
  line: (depth: number, text: string) => void,
  id: string,
  b: Bounds,
  extra: string,
): void {
  line(3, `<bpmndi:BPMNShape id="di-${id}" bpmnElement="${id}"${extra}>`);
  line(
    4,
    `<dc:Bounds x="${num(b.x)}" y="${num(b.y)}" width="${num(b.width)}" height="${num(b.height)}" />`,
  );
  line(3, '</bpmndi:BPMNShape>');
}

function waypoint(p: Point): string {
  return `<di:waypoint x="${num(p.x)}" y="${num(p.y)}" />`;
}

function num(n: number): string {
  return String(Math.round(n));
}

// Characters XML 1.0 does not allow at all.
const ILLEGAL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f￾￿]/g;

function text(value: string): string {
  return value
    .replace(ILLEGAL, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function attr(value: string): string {
  return text(value)
    .replace(/"/g, '&quot;')
    .replace(/\t/g, '&#9;')
    .replace(/\n/g, '&#10;')
    .replace(/\r/g, '&#13;');
}
